import React, { useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import SectionHeader from "./SectionHeader";
import PhotoUploadBox from "./PhotoUploadBox";
import { useTranslation } from "react-i18next";
import { getUserDateFormat } from "../../utils/formatDate";

export default function DriverDetailsSection({
  formData,
  handleInputChange,
  selectedImage,
  setSelectedImage,
  setFormData,
  imageError,
  setImageError,
  hasError,
  getErrorMessage,
  inputStyles,
  redAsteriskStyle,
  isUploaded,
  preview,
  allVehicles = [],
  supervisors = [],
  onClose,
  isEditing = false,
}) {
  const { t } = useTranslation();
  const userDateFormat = getUserDateFormat();

  const getDayjsFormat = (format) => {
    const formatMap = {
      "DD/MM/YYYY": "DD/MM/YYYY",
      "MM/DD/YYYY": "MM/DD/YYYY",
      "YYYY/MM/DD": "YYYY/MM/DD",
      "DD-MM-YYYY": "DD-MM-YYYY",
      "MM-DD-YYYY": "MM-DD-YYYY",
      "YYYY-MM-DD": "YYYY-MM-DD",
      "DD.MM.YYYY": "DD.MM.YYYY",
      "MM.DD.YYYY": "MM.DD.YYYY",
      "YYYY.MM.DD": "YYYY.MM.DD",
    };
    return formatMap[format] || "DD/MM/YYYY";
  };

  const handleImageUpload = (file, error) => {
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(file);
      setImageError("");
      setFormData((prev) => ({ ...prev, imagePreview: imageUrl }));
    } else {
      setSelectedImage(null);
      setImageError(error);
    }
  };

  const handleRemoveImage = () => {
    if (selectedImage instanceof File && formData.imagePreview) {
      URL.revokeObjectURL(formData.imagePreview);
    }
    setSelectedImage(null);
    setFormData((prev) => ({ ...prev, imagePreview: null }));
    setImageError("");
  };

  useEffect(() => {
    return () => {
      if (formData.imagePreview && selectedImage instanceof File) {
        URL.revokeObjectURL(formData.imagePreview);
      }
    };
  }, [formData.imagePreview, selectedImage]);

  const titleStyle = {
    mb: 1,
    color: "#1F242E",
    fontFamily: "Roboto",
    fontWeight: 600,
    fontSize: "20px",
  };

  const editingInputStyles = {
    ...inputStyles,
    ...(isEditing && {
      "& .MuiOutlinedInput-root": {
        ...inputStyles["& .MuiOutlinedInput-root"],
        backgroundColor: "#FFFFFF",
        border: "2px solid #183059",
      },
    }),
  };

  const fieldSx = (fieldName) => ({
    ...inputStyles,
    ...(isEditing && {
      "& .MuiOutlinedInput-root": {
        ...inputStyles["& .MuiOutlinedInput-root"],
        backgroundColor: "#FFFFFF",
        border: "2px solid #183059",
      },
    }),
    ...(hasError(fieldName) && {
      "& .MuiOutlinedInput-root": {
        ...inputStyles["& .MuiOutlinedInput-root"],
        ...(isEditing && { backgroundColor: "#FFFFFF" }),
        border: "2px solid #d32f2f",
      },
    }),
  });

  const inputPropSx = {
    fontFamily: "Roboto",
    fontWeight: 500,
    fontSize: "20px",
    color: "#111214",
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* ══════════════ PERSONAL DETAILS ══════════════ */}
      <Box>
        <SectionHeader title={t("Personal Details")} onClose={onClose} isEditing={isEditing} />
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Col 1 */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={titleStyle}>
                  {t("First Name")} <span style={redAsteriskStyle}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  value={formData.firstName}
                  onChange={handleInputChange}
                  id="firstName"
                  name="firstName"
                  placeholder="Enter first name (e.g. John)"
                  variant="outlined"
                  sx={fieldSx("firstName")}
                  InputProps={{ required: false, sx: inputPropSx }}
                  error={hasError("firstName")}
                  helperText={getErrorMessage("firstName")}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={titleStyle}>
                  {t("Driver ID")} <span style={redAsteriskStyle}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  value={formData.driverId}
                  onChange={handleInputChange}
                  id="driverId"
                  name="driverId"
                  placeholder="e.g. DRV1023"
                  variant="outlined"
                  sx={fieldSx("driverId")}
                  InputProps={{ sx: inputPropSx }}
                  error={hasError("driverId")}
                  helperText={getErrorMessage("driverId")}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={titleStyle}>
                  {t("Email")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.email}
                  onChange={handleInputChange}
                  id="email"
                  name="email"
                  placeholder="e.g. john.doe@example.com"
                  variant="outlined"
                  sx={fieldSx("email")}
                  InputProps={{ sx: inputPropSx }}
                  error={hasError("email")}
                  helperText={getErrorMessage("email")}
                />
              </Box>
            </Grid>

            {/* Col 2 */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={titleStyle}>
                  {t("Last Name")} <span style={redAsteriskStyle}>*</span>
                </Typography>
                <TextField
                  required
                  fullWidth
                  value={formData.lastName}
                  onChange={handleInputChange}
                  id="lastName"
                  name="lastName"
                  placeholder={`${t("Enter your last name")} (e.g. Doe)`}
                  variant="outlined"
                  sx={fieldSx("lastName")}
                  InputProps={{ required: false, sx: inputPropSx }}
                  error={hasError("lastName")}
                  helperText={getErrorMessage("lastName")}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={titleStyle}>
                  {t("Phone Number")}
                </Typography>
                <TextField
                  fullWidth
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder="e.g. 9876543210"
                  variant="outlined"
                  sx={fieldSx("phoneNumber")}
                  InputProps={{ sx: inputPropSx }}
                  error={hasError("phoneNumber")}
                  helperText={getErrorMessage("phoneNumber")}
                />
              </Box>{" "}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={titleStyle}>
                  {t("Date Of Birth")} <span style={redAsteriskStyle}>*</span>
                </Typography>
                <DatePicker
                  value={
                    formData.dateOfBirth
                      ? dayjs(formData.dateOfBirth, "YYYY-MM-DD")
                      : null
                  }
                  onChange={(newValue) =>
                    handleInputChange({
                      target: {
                        name: "dateOfBirth",
                        value:
                          newValue && newValue.isValid()
                            ? newValue.format("YYYY-MM-DD")
                            : "",
                      },
                    })
                  }
                  maxDate={dayjs()}
                  minDate={dayjs("1900-01-01")}
                  format={getDayjsFormat(userDateFormat)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: getDayjsFormat(userDateFormat),
                      error: hasError("dateOfBirth") || hasError("dob"),
                      helperText:
                        getErrorMessage("dateOfBirth") ||
                        getErrorMessage("dob"),
                      sx: {
                        ...inputStyles,
                        ...(isEditing && {
                          "& .MuiOutlinedInput-root": {
                            ...inputStyles["& .MuiOutlinedInput-root"],
                            backgroundColor: "#FFFFFF",
                            border: "2px solid #183059",
                          },
                        }),
                        "& .MuiOutlinedInput-input": inputPropSx,
                      },
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* Col 3 – Photo */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <PhotoUploadBox
                  selectedImage={selectedImage}
                  onImageUpload={handleImageUpload}
                  onRemoveImage={handleRemoveImage}
                  error={imageError}
                  isUploaded={isUploaded}
                  preview={preview}
                />
              </Box>
            </Grid>

            {/* ── Address Section: titles row ── */}
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#647490",
                  mb: 1.5,
                  lineHeight: "32px",
                }}
              >
                {t("Present Address")}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex",justifyContent:"space-between", alignItems: "center", gap: 2, mb: 1.5 }}>
                <Typography
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "#647490",
                    lineHeight: "32px",
                  }}
                >
                  {t("Permanent Address")}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "#414958",
                        "&.Mui-checked": { color: "#20A8E0" },
                      }}
                      checked={formData.sameAsPresentAddress}
                      onChange={handleInputChange}
                      name="sameAsPresentAddress"
                    />
                  }
                  label={t("Same as Present Address")}
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": {
                      color: formData.sameAsPresentAddress ? "#20A8E0" : "#414958",
                    },
                  }}
                />
              </Box>
            </Grid>

            {/* ── Address row ── */}
            <Grid item xs={12} md={6} sx={{ pt: "0 !important" }}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("Address")}
              </Typography>
              <TextField
                fullWidth
                value={formData.presentAddress}
                onChange={handleInputChange}
                name="presentAddress"
                placeholder="Enter present address"
                variant="outlined"
                sx={fieldSx("presentAddress")}
                InputProps={{ sx: inputPropSx }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ pt: "0 !important" }}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("Address")}
              </Typography>
              <TextField
                fullWidth
                value={formData.permanentAddress}
                onChange={handleInputChange}
                name="permanentAddress"
                placeholder="Enter permanent address"
                variant="outlined"
                disabled={formData.sameAsPresentAddress}
                sx={fieldSx("permanentAddress")}
                InputProps={{ sx: inputPropSx }}
              />
            </Grid>

            {/* ── City row ── */}
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("City")} <span style={redAsteriskStyle}>*</span>
              </Typography>
              <TextField
                required
                fullWidth
                value={formData.presentCity}
                onChange={handleInputChange}
                name="presentCity"
                placeholder="Enter city"
                variant="outlined"
                sx={fieldSx("presentCity")}
                InputProps={{ sx: inputPropSx }}
                error={hasError("presentCity")}
                helperText={getErrorMessage("presentCity")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("Pincode")}
              </Typography>
              <TextField
                fullWidth
                value={formData.presentPincode}
                onChange={handleInputChange}
                name="presentPincode"
                placeholder="Enter pincode"
                variant="outlined"
                sx={editingInputStyles}
                InputProps={{ sx: inputPropSx }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("City")} <span style={redAsteriskStyle}>*</span>
              </Typography>
              <TextField
                required
                fullWidth
                value={formData.permanentCity}
                onChange={handleInputChange}
                name="permanentCity"
                placeholder="Enter city"
                variant="outlined"
                disabled={formData.sameAsPresentAddress}
                sx={fieldSx("permanentCity")}
                InputProps={{ sx: inputPropSx }}
                error={hasError("permanentCity")}
                helperText={getErrorMessage("permanentCity")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("Pincode")}
              </Typography>
              <TextField
                fullWidth
                value={formData.permanentPincode}
                onChange={handleInputChange}
                name="permanentPincode"
                placeholder="Enter pincode"
                variant="outlined"
                disabled={formData.sameAsPresentAddress}
                sx={editingInputStyles}
                InputProps={{ sx: inputPropSx }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ══════════════ LICENSE DETAILS ══════════════ */}
      <Box>
        <SectionHeader title={t("License Details")} isEditing={isEditing} />
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* License ID – 3 cols */}
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("Driving License ID")} <span style={redAsteriskStyle}>*</span>
              </Typography>
              <TextField
                required
                fullWidth
                value={formData.drivingLicenseId}
                onChange={handleInputChange}
                id="drivingLicenseId"
                name="drivingLicenseId"
                placeholder="e.g. KA0120190001234"
                variant="outlined"
                sx={fieldSx("drivingLicenseId")}
                InputProps={{ required: false, sx: inputPropSx }}
                error={hasError("drivingLicenseId")}
                helperText={getErrorMessage("drivingLicenseId")}
              />
            </Grid>

            {/* DL Expiry Date – 3 cols */}
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("Driving License Expiry Date")}
              </Typography>
              <DatePicker
                value={
                  formData.licenseExpiryDate
                    ? dayjs(formData.licenseExpiryDate, "YYYY-MM-DD")
                    : null
                }
                onChange={(newValue) =>
                  handleInputChange({
                    target: {
                      name: "licenseExpiryDate",
                      value:
                        newValue && newValue.isValid()
                          ? newValue.format("YYYY-MM-DD")
                          : "",
                    },
                  })
                }
                minDate={dayjs()}
                format={getDayjsFormat(userDateFormat)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    placeholder: getDayjsFormat(userDateFormat),
                    error: hasError("licenseExpiryDate"),
                    helperText: getErrorMessage("licenseExpiryDate"),
                    sx: {
                      ...inputStyles,
                      ...(isEditing && {
                        "& .MuiOutlinedInput-root": {
                          ...inputStyles["& .MuiOutlinedInput-root"],
                          backgroundColor: "#FFFFFF",
                          border: "2px solid #183059",
                        },
                      }),
                      "& .MuiOutlinedInput-input": inputPropSx,
                    },
                  },
                }}
              />
            </Grid>

            {/* Additional Details – 6 cols */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("Additional Details")}
              </Typography>
              <TextField
                fullWidth
                value={formData.moreDetails}
                onChange={handleInputChange}
                id="moreDetails"
                name="moreDetails"
                placeholder={t("Add any additional information")}
                variant="outlined"
                sx={editingInputStyles}
                InputProps={{ sx: inputPropSx }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* ══════════════ ASSIGNED VEHICLES ══════════════ */}
      <Box>
        <SectionHeader title={t("Assigned Vehicles")} isEditing={isEditing} />
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 120,
            gap: 2,
          }}
        >
          {!formData.vehicle_id ? (
            <>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "#647490",
                  textAlign: "center",
                }}
              >
                {t(
                  "No vehicle assigned yet. Select a vehicle to start allocating trips.",
                )}
              </Typography>
              <FormControl sx={{ minWidth: 240 }}>
                <Select
                  displayEmpty
                  value={formData.vehicle_id || ""}
                  onChange={(e) =>
                    handleInputChange({
                      target: { name: "vehicle_id", value: e.target.value },
                    })
                  }
                  sx={{
                    borderRadius: "8px",
                    height: "43px",
                    border: "1px solid #B7BFCC",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    display: "none",
                  }}
                >
                  {allVehicles.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name || v.device_id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                onClick={() =>
                  setFormData((prev) => ({
                    ...prev,
                    _showVehicleSelect: !prev._showVehicleSelect,
                  }))
                }
                sx={{
                  width: 174,
                  height: 36,
                  borderRadius: "8px",
                  gap: "10px",
                  paddingTop: "8px",
                  paddingRight: "16px",
                  paddingBottom: "8px",
                  paddingLeft: "16px",
                  backgroundColor: "#20A8E0",
                  color: "#FFFFFF",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "14px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#1890c0" },
                }}
              >
                {t("Assign New Vehicle")}
              </Button>
              {formData._showVehicleSelect && (
                <FormControl sx={{ minWidth: 300, mt: 1 }}>
                  <Select
                    displayEmpty
                    value={formData.vehicle_id || ""}
                    onChange={(e) => {
                      handleInputChange({
                        target: { name: "vehicle_id", value: e.target.value },
                      });
                      setFormData((prev) => ({
                        ...prev,
                        _showVehicleSelect: false,
                      }));
                    }}
                    sx={{
                      borderRadius: "8px",
                      height: "43px",
                      border: "1px solid #B7BFCC",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    }}
                  >
                    <MenuItem value="" sx={{ color: "#414958a0" }}>
                      {t("Select a Vehicle")}
                    </MenuItem>
                    {allVehicles.map((v) => (
                      <MenuItem key={v.id} value={v.id}>
                        {v.name || v.device_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: "16px",
                  color: "#183059",
                }}
              >
                {t("Assigned")}:{" "}
                {allVehicles.find((v) => v.id === formData.vehicle_id)?.name ||
                  allVehicles.find((v) => v.id === formData.vehicle_id)
                    ?.device_id ||
                  formData.vehicle_id}
              </Typography>
              <Button
                onClick={() =>
                  handleInputChange({
                    target: { name: "vehicle_id", value: "" },
                  })
                }
                sx={{
                  height: 36,
                  borderRadius: "8px",
                  px: 2,
                  backgroundColor: "#F2F2F3",
                  color: "#1F242E",
                  border: "1px solid #414958",
                  textTransform: "none",
                  fontSize: "14px",
                  "&:hover": { backgroundColor: "#E5E5E6" },
                }}
              >
                {t("Change")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* ══════════════ ASSIGNED SUPERVISOR ══════════════ */}
      <Box>
        <SectionHeader title={t("Assigned Supervisor")} isEditing={isEditing} />
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={titleStyle}>
                {t("Name")}
              </Typography>
              <FormControl fullWidth>
                <Select
                  displayEmpty
                  value={formData.supervisorId || ""}
                  onChange={(e) =>
                    handleInputChange({
                      target: { name: "supervisorId", value: e.target.value },
                    })
                  }
                  IconComponent={(props) => (
                    <svg
                      {...props}
                      width="18"
                      height="10"
                      viewBox="0 0 18 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ pointerEvents: "none", marginRight: "12px" }}
                    >
                      <path
                        d="M17 1L9 9L1 1"
                        stroke="#20A8E0"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                  sx={{
                    borderRadius: "8px",
                    height: "55px",
                    border: "2px solid #183059",
                    backgroundColor: isEditing ? "#FFFFFF" : "transparent",
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    fontSize: "20px",
                    color: "#111214",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  }}
                >
                  <MenuItem
                    value=""
                    sx={{
                      color: "#414958a0",
                      fontFamily: "Roboto",
                      fontWeight: 400,
                      fontSize: "16px",
                    }}
                  >
                    {t("Select Supervisor")}
                  </MenuItem>
                  {supervisors.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name || s.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
